import React, { useState, useRef, useEffect } from 'react';
import { useScroll } from 'hooks/useScroll';
import { formatDate, formatDateForString } from 'utils/formatter';
import { convertFromString } from 'utils/date';
import NumberLabel from 'components/features/utils/NumberLabel';
import Icon from 'components/commons/icon/Icon';
import OrderItem from './OrderItem';
import PaginationOrderList, {
  PageParamType,
  updateRoutigList,
} from './PaginationOrderList';
import { DisplayableAssetTypeModalButton } from '../home/PortfolioStuffing/asset/AssetOrganizedPortfolioForeign';
import { OrderFilterModal } from '../modal/OrderFilterModal';

import * as consts from 'config/consts';
import * as api from 'config/api';

export const initialPageNum = 1;

export type OrderFilterState = {
  FromYM: string;
  ToYM: string;
  chkProductAll: boolean;
  chkStock: boolean;
  chkFforeignStock: boolean;
  chkInvestmentTrust: boolean;
  chkEtfEtn: boolean;
  chkForeignEtfEtn: boolean;
  chkReit: boolean;
  chkForeignReit: boolean;
  chkEb: boolean;
  chkForeignEb: boolean;
  chkUnlistedStock: boolean;
  chkBond: boolean;
  chkForeignBond: boolean;
  chkRealEstate: boolean;
  chkOther: boolean;
  chkOrderAll: boolean;
  chkSettlement: boolean;
  chkSettlementSpotSell: boolean;
  chkSettlementMerginBuy: boolean;
  chkSettlementMerginSell: boolean;
  chkMerginBuy: boolean;
  chkMerginSell: boolean;
  chkSpotBuy: boolean;
  chkSecurityAll: boolean;
  chkSecuritySBI: boolean;
  chkSecurityRakuten: boolean;
  chkSecurityAuKabuCom: boolean;
  chkSecurityMatsui: boolean;
  chkSecurityNomura: boolean;
  [key: string]: string | boolean;
};

const initialState: OrderFilterState = {
  FromYM: '',
  ToYM: '',
  chkProductAll: true,
  chkStock: false,
  chkFforeignStock: false,
  chkInvestmentTrust: false,
  chkEtfEtn: false,
  chkForeignEtfEtn: false,
  chkReit: false,
  chkForeignReit: false,
  chkEb: false,
  chkForeignEb: false,
  chkUnlistedStock: false,
  chkBond: false,
  chkForeignBond: false,
  chkRealEstate: false,
  chkOther: false,
  chkOrderAll: true,
  chkSettlement: false,
  chkSettlementSpotSell: false,
  chkSettlementMerginBuy: false,
  chkSettlementMerginSell: false,
  chkMerginBuy: false,
  chkMerginSell: false,
  chkSpotBuy: false,
  chkSecurityAll: true,
  chkSecuritySBI: false,
  chkSecurityRakuten: false,
  chkSecurityAuKabuCom: false,
  chkSecurityMatsui: false,
  chkSecurityNomura: false,
};

function formatYM(ym: string) {
  const yyyy = ym.substr(0, 4);
  const mm = ym.substr(4, 2);
  return `${yyyy}年${Number(mm).toString()}月`;
}

function isDisplay(enabled: boolean) {
  return enabled ? '' : 'hidden';
}

function getTargetFromToList(
  from: string | undefined | null,
  to: string | undefined | null
): string[] {
  const arr: string[] = [];
  if (from === null && to === null) {
    const today = new Date();
    for (let n = 0; n < 12; n++) {
      const dt = new Date(today.getFullYear() - 1, today.getMonth() + 1 + n, 1);
      arr[n] =
        dt.getFullYear().toString() +
        ('0' + (dt.getMonth() + 1).toString()).slice(-2);
    }
  } else {
    const dateFrom_ = convertFromString(
      formatDateForString(from, 'yyyyMMdd'),
      false
    );
    const dateTo_ = convertFromString(
      formatDateForString(to, 'yyyyMMdd'),
      false
    );
    // MEMO: 日の考慮は不要なため、1日に変換する。
    const dateTo = new Date(dateTo_.setDate(1));
    for (
      let dateFrom = dateFrom_;
      dateFrom < dateTo;
      dateFrom.setMonth(dateFrom.getMonth() + 1)
    ) {
      arr.push(formatDate(dateFrom, 'yyyyMM'));
    }
    arr.push(formatDate(dateTo, 'yyyyMM'));
  }
  return arr;
}

function chkBroker(
  brokers: api.GetUserBrokerResultItem[],
  brokerID: string,
  settingState: boolean
) {
  return brokers.find(r => r.BrokerID === brokerID) ? settingState : false;
}

export function getOrderSetting(
  brokers: api.GetUserBrokerResultItem[] | undefined
): OrderFilterState {
  let setting = localStorage.getItem('order_setting');
  if (setting === null) {
    setting = JSON.stringify(initialState);
    localStorage.setItem('order_setting', setting);
  }
  const ret_ = JSON.parse(setting) as OrderFilterState;

  // MEMO: MK_TW2-1230で絞込み条件の削除と追加があったため、既存ユーザのローカルストレージの形式を最新にする
  const ret = {
    FromYM: ret_.FromYM || '',
    ToYM: ret_.ToYM || '',
    chkProductAll: ret_.chkProductAll || false,
    chkStock: ret_.chkStock || false,
    chkFforeignStock: ret_.chkFforeignStock || false,
    chkInvestmentTrust: ret_.chkInvestmentTrust || false,
    chkEtfEtn: ret_.chkEtfEtn || false,
    chkForeignEtfEtn: ret_.chkForeignEtfEtn || false,
    chkReit: ret_.chkReit || false,
    chkForeignReit: ret_.chkForeignReit || false,
    chkEb: ret_.chkEb || false,
    chkForeignEb: ret_.chkForeignEb || false,
    chkUnlistedStock: ret_.chkUnlistedStock || false,
    chkBond: ret_.chkBond || false,
    chkForeignBond: ret_.chkForeignBond || false,
    chkRealEstate: ret_.chkRealEstate || false,
    chkOther: ret_.chkOther || false,
    chkOrderAll: ret_.chkOrderAll || false,
    chkSettlement: ret_.chkSettlement || false,
    chkSettlementSpotSell: ret_.chkSettlementSpotSell || false,
    chkSettlementMerginBuy: ret_.chkSettlementMerginBuy || false,
    chkSettlementMerginSell: ret_.chkSettlementMerginSell || false,
    chkMerginBuy: ret_.chkMerginBuy || false,
    chkMerginSell: ret_.chkMerginSell || false,
    chkSpotBuy: ret_.chkSpotBuy || false,
    chkSecurityAll: ret_.chkSecurityAll || false,
    chkSecuritySBI: ret_.chkSecuritySBI || false,
    chkSecurityRakuten: ret_.chkSecurityRakuten || false,
    chkSecurityAuKabuCom: ret_.chkSecurityAuKabuCom || false,
    chkSecurityMatsui: ret_.chkSecurityMatsui || false,
    chkSecurityNomura: ret_.chkSecurityNomura || false,
  } as OrderFilterState;

  if (brokers) {
    ret.chkSecuritySBI = chkBroker(
      brokers,
      consts.MONOBrokerIds.SBI証券,
      ret.chkSecuritySBI
    );
    ret.chkSecurityRakuten = chkBroker(
      brokers,
      consts.MONOBrokerIds.楽天証券,
      ret.chkSecurityRakuten
    );
    ret.chkSecurityAuKabuCom = chkBroker(
      brokers,
      consts.MONOBrokerIds.三菱UFJeスマート証券,
      ret.chkSecurityAuKabuCom
    );
    ret.chkSecurityMatsui = chkBroker(
      brokers,
      consts.MONOBrokerIds.松井証券,
      ret.chkSecurityMatsui
    );
    ret.chkSecurityNomura = chkBroker(
      brokers,
      // MEMO: 定義上のundefined対応。MONOのデータしか扱わないためfalseになることはない。
      consts.MONOBrokerIds.野村證券 ?? 'NOMURA',
      ret.chkSecurityNomura
    );
  }

  return ret;
}

const getPeriodQueryFormat = (val: string): string | null => {
  const regex = /^(\d{4})(\d{2})$/;
  const result = regex.exec(val);
  if (result === null) return null;
  return `${result[1]}/${result[2]}`;
};

type OrderListProps = {
  brokers: api.GetUserBrokerResultItem[] | undefined;
  setAPILoaded: () => void;
};

type OrdersItemProps = {
  tradeTime: string | null;
  symbol: string;
  symbolName: string;
  company: string;
  qty: number | null;
  price: number | null;
  openPriceUSD: number | null;
  side: number;
  tradeType: number;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  accountType: number;
  cashMargin: number;
  productType: number;
  currencyUnit: string | null;
};

type OrdersProps = {
  items: OrdersItemProps[];
  totalProfitOrLoss: number | null;
  oldestDate: string;
  latestDate: string;
  totalPageNum: number;
  pageNum: number;
};

const OrderList: React.FC<OrderListProps> = props => {
  const urlParams = new URLSearchParams(window.location.search);
  const selectPageNum = urlParams.get('page')
    ? Number(urlParams.get('page'))
    : initialPageNum;

  const brokers = props.brokers?.filter((item, index, arr) => {
    return arr.findIndex(item2 => item.BrokerID === item2.BrokerID) === index;
  });
  const [showModal, setShowModal] = useState(false);
  const [orders, setOrders] = useState<OrdersProps | null>(null);

  const orderFilter: OrderFilterState = getOrderSetting(brokers);

  // ページング
  const [startPage, setStartPage] = useState<number>(initialPageNum);
  const [selectPage, setSelectPage] = useState<number>(selectPageNum);
  const [totalPageCount, setTotalPageCount] = useState<number | undefined>(
    undefined
  );

  const unmounted = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    if (unmounted.current) return;

    (async () => {
      const res = await getOrders(selectPageNum, brokers);
      setOrders(res);
      setTotalPageCount(res.totalPageNum);
    })();

    updateRoutigList(selectPageNum.toString());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orders !== null && orders.items.length > 0) {
      props.setAPILoaded();
    }
  }, [orders, props]);

  const updateOrderFilter = (filter: OrderFilterState) => {
    localStorage.setItem('order_setting', JSON.stringify(filter));
  };

  // 検索結果取得
  const refetchOrders = (pageNum = selectPageNum): void => {
    (async () => {
      const res = await getOrders(pageNum, brokers);
      setOrders(res);
      setTotalPageCount(res.totalPageNum);
      setSelectPage(res.pageNum);
      if (res.pageNum === selectPageNum) setStartPage(selectPageNum);
    })();
  };

  const selectProducts = [
    {
      id: consts.productTypes.国内株式,
      text: '国内株',
      name: 'chkStock',
      checked: orderFilter.chkStock,
    },
    {
      id: consts.productTypes.外国株式,
      text: '外国株',
      name: 'chkFforeignStock',
      checked: orderFilter.chkFforeignStock,
    },
    {
      id: consts.productTypes.投資信託,
      text: '投信',
      name: 'chkInvestmentTrust',
      checked: orderFilter.chkInvestmentTrust,
    },
    {
      id: consts.productTypes['国内ETF・ETN'],
      text: '国内ETF・ETN',
      name: 'chkEtfEtn',
      checked: orderFilter.chkEtfEtn,
    },
    {
      id: consts.productTypes['外国ETF・ETN'],
      text: '外国ETF・ETN',
      name: 'chkForeignEtfEtn',
      checked: orderFilter.chkForeignEtfEtn,
    },
    {
      id: consts.productTypes.国内REIT,
      text: '国内REIT',
      name: 'chkReit',
      checked: orderFilter.chkReit,
    },
    {
      id: consts.productTypes.外国REIT,
      text: '外国REIT',
      name: 'chkForeignReit',
      checked: orderFilter.chkForeignReit,
    },
    {
      id: consts.productTypes.国内EB,
      text: '国内EB',
      name: 'chkEb',
      checked: orderFilter.chkEb,
    },
    {
      id: consts.productTypes.外国EB,
      text: '外国EB',
      name: 'chkForeignEb',
      checked: orderFilter.chkForeignEb,
    },
    {
      id: consts.productTypes.未上場株式,
      text: '未上場株',
      name: 'chkUnlistedStock',
      checked: orderFilter.chkUnlistedStock,
    },
    {
      id: consts.productTypes.国内債券,
      text: '国内債券',
      name: 'chkBond',
      checked: orderFilter.chkBond,
    },
    {
      id: consts.productTypes.外国債券,
      text: '外国債券',
      name: 'chkForeignBond',
      checked: orderFilter.chkForeignBond,
    },
    {
      id: consts.productTypes.不動産,
      text: '不動産',
      name: 'chkRealEstate',
      checked: orderFilter.chkRealEstate,
    },
    {
      id: consts.productTypes.その他,
      text: 'その他',
      name: 'chkOther',
      checked: orderFilter.chkOther,
    },
  ];

  const selectOrders = [
    {
      text: '現物売り',
      name: 'chkSettlementSpotSell',
      isReturn: true,
      checked: orderFilter.chkSettlementSpotSell,
    },
    {
      text: '現物買い',
      name: 'chkSpotBuy',
      isReturn: false,
      checked: orderFilter.chkSpotBuy,
    },
    {
      text: '信用返売',
      name: 'chkSettlementMerginSell',
      isReturn: true,
      checked: orderFilter.chkSettlementMerginSell,
    },
    {
      text: '信用返買',
      name: 'chkSettlementMerginBuy',
      isReturn: true,
      checked: orderFilter.chkSettlementMerginBuy,
    },
    {
      text: '信用売り',
      name: 'chkMerginSell',
      isReturn: false,
      checked: orderFilter.chkMerginSell,
    },
    {
      text: '信用買い',
      name: 'chkMerginBuy',
      isReturn: false,
      checked: orderFilter.chkMerginBuy,
    },
  ];

  const selectBrokers = [
    {
      id: consts.MONOBrokerIds.SBI証券,
      name: 'chkSecuritySBI',
      checked: orderFilter.chkSecuritySBI,
    },
    {
      id: consts.MONOBrokerIds.楽天証券,
      name: 'chkSecurityRakuten',
      checked: orderFilter.chkSecurityRakuten,
    },
    {
      id: consts.MONOBrokerIds.松井証券,
      name: 'chkSecurityMatsui',
      checked: orderFilter.chkSecurityMatsui,
    },
    {
      id: consts.MONOBrokerIds.三菱UFJeスマート証券,
      name: 'chkSecurityAuKabuCom',
      checked: orderFilter.chkSecurityAuKabuCom,
    },
    {
      id: consts.MONOBrokerIds.野村證券,
      name: 'chkSecurityNomura',
      checked: orderFilter.chkSecurityNomura,
    },
  ];

  const clsSelected =
    'inline-flex border-t border-l border-b border-r rounded border-solid border-gray-300 bg-gray-100 ml-1 mb-1 py-1';

  const selectProductItems = selectProducts.map((item, index) => {
    return (
      <div
        className={`${isDisplay(item.checked)} ${clsSelected}`}
        key={index}
        style={{ color: '#64748B' }}>
        <div className="px-2">{item.text}</div>
        <div
          className="pr-2 cursor-pointer"
          data-name={item.name}
          onClick={e => {
            const updFilter = {
              ...orderFilter,
              ...{ [e.currentTarget.dataset.name as string]: false },
            };
            updateOrderFilter(updFilter);
            updateRoutigList(initialPageNum.toString());
            refetchOrders();
          }}>
          <Icon className="leading-tight" name="close" />
        </div>
      </div>
    );
  });

  const selectOrderItems = selectOrders.map((item, index) => {
    return (
      <div
        className={`${isDisplay(item.checked)} ${clsSelected}`}
        key={index}
        style={{ color: '#64748B' }}>
        <div
          className={`${isDisplay(
            item.isReturn
          )} ml-1 px-1 bg-gray-500 text-center text-white rounded-full`}>
          決済
        </div>
        <div className="px-2">{item.text}</div>
        <div
          className="pr-2 cursor-pointer"
          data-name={item.name}
          onClick={e => {
            const updFilter = {
              ...orderFilter,
              ...{ [e.currentTarget.dataset.name as string]: false },
            };
            // 区分指定で、決済注文の何かしらが解除あれば、[決済のみ表示]を解除
            if (
              !updFilter.chkSettlementMerginBuy ||
              !updFilter.chkSettlementMerginSell ||
              !updFilter.chkSettlementSpotSell
            ) {
              updFilter.chkSettlement = false;
            }
            updateOrderFilter(updFilter);
            updateRoutigList(initialPageNum.toString());
            refetchOrders();
          }}>
          <Icon className="leading-tight" name="close" />
        </div>
      </div>
    );
  });

  const selectSecurityItems = brokers?.map((item, index) => {
    const broker = selectBrokers.find(r => r.id === item.BrokerID);
    return !broker ? (
      undefined
    ) : (
      <div
        className={`${isDisplay(broker.checked)} ${clsSelected}`}
        key={index}
        style={{ color: '#64748B' }}>
        <div className="px-2">{item.BrokerName}</div>
        <div
          className="pr-2 cursor-pointer"
          data-name={broker.name}
          onClick={e => {
            const updFilter = {
              ...orderFilter,
              ...{ [e.currentTarget.dataset.name as string]: false },
            };
            updateOrderFilter(updFilter);
            updateRoutigList(initialPageNum.toString());
            refetchOrders();
          }}>
          <Icon className="leading-tight" name="close" />
        </div>
      </div>
    );
  });

  const targetYM = getTargetFromToList(orders?.oldestDate, orders?.latestDate);

  const removeOrderSetting = (): void => {
    updateOrderFilter({
      FromYM: targetYM ? targetYM[0] : '',
      ToYM: targetYM ? targetYM[targetYM.length - 1] : '',
      chkProductAll: true,
      chkStock: false,
      chkFforeignStock: false,
      chkInvestmentTrust: false,
      chkEtfEtn: false,
      chkForeignEtfEtn: false,
      chkReit: false,
      chkForeignReit: false,
      chkEb: false,
      chkForeignEb: false,
      chkUnlistedStock: false,
      chkBond: false,
      chkForeignBond: false,
      chkRealEstate: false,
      chkOther: false,
      chkOrderAll: true,
      chkSettlement: false,
      chkSettlementSpotSell: false,
      chkSettlementMerginBuy: false,
      chkSettlementMerginSell: false,
      chkMerginBuy: false,
      chkMerginSell: false,
      chkSpotBuy: false,
      chkSecurityAll: true,
      chkSecuritySBI: false,
      chkSecurityRakuten: false,
      chkSecurityAuKabuCom: false,
      chkSecurityMatsui: false,
      chkSecurityNomura: false,
    });
    refetchOrders();
  };

  const onChangePage = (newPageParam: PageParamType): void => {
    refetchOrders(newPageParam.selectPage);
    if (startPage !== newPageParam.startPage)
      return setStartPage(newPageParam.startPage);
  };

  useScroll();

  return (
    <div
      onClick={() => {
        showModal && setShowModal(false);
      }}>
      {showModal === true && (
        <OrderFilterModal
          {...{
            brokers,
            orderFilter,
            targetYM,
            getOrderSetting,
            updateOrderFilter,
            setShowModal,
            refetchOrders,
          }}
        />
      )}
      <div className="ui-card m-2 text-sm text-gray-500 px-2">
        <div className="flex justify-between py-1 ">
          <div className="font-bold p-2 text-base" style={{ color: '#334155' }}>
            表示条件
          </div>
          <div
            className="p-2 text-link ml-auto cursor-pointer"
            style={{
              color:
                targetYM !== undefined &&
                orderFilter.FromYM === targetYM[0] &&
                orderFilter.ToYM === targetYM[targetYM.length - 1] &&
                !orderFilter.chkSettlement &&
                !orderFilter.chkSettlementSpotSell &&
                !orderFilter.chkSettlementMerginBuy &&
                !orderFilter.chkSettlementMerginSell &&
                !orderFilter.chkMerginBuy &&
                !orderFilter.chkMerginSell &&
                !orderFilter.chkSpotBuy &&
                !orderFilter.chkSecuritySBI &&
                !orderFilter.chkSecurityRakuten &&
                !orderFilter.chkSecurityAuKabuCom &&
                !orderFilter.chkSecurityMatsui &&
                !orderFilter.chkSecurityNomura &&
                !orderFilter.chkStock &&
                !orderFilter.chkFforeignStock &&
                !orderFilter.chkInvestmentTrust &&
                !orderFilter.chkEtfEtn &&
                !orderFilter.chkForeignEtfEtn &&
                !orderFilter.chkReit &&
                !orderFilter.chkForeignReit &&
                !orderFilter.chkEb &&
                !orderFilter.chkForeignEb &&
                !orderFilter.chkUnlistedStock &&
                !orderFilter.chkBond &&
                !orderFilter.chkForeignBond &&
                !orderFilter.chkRealEstate &&
                !orderFilter.chkOther
                  ? '#CBD5E1'
                  : '#64748B',
            }}
            onClick={removeOrderSetting}>
            条件をクリア
          </div>
          <div
            className="p-2 text-link cursor-pointer"
            onClick={() => {
              setShowModal(true);
            }}>
            変更する
          </div>
        </div>

        <div
          className={`${isDisplay(
            !!(orderFilter.FromYM || orderFilter.ToYM)
          )} ml-2 ui-border-b`}>
          <div
            className={`${clsSelected} px-3 font-bold`}
            style={{ color: '#334155' }}>
            <div>{formatYM(orderFilter.FromYM)}</div>
            <div className="px-3">～</div>
            <div>{formatYM(orderFilter.ToYM)}</div>
            <div
              className="pl-2 cursor-pointer"
              onClick={() => {
                setShowModal(true);
              }}>
              <Icon name="calendar" />
            </div>
          </div>
          <div>{selectProductItems}</div>
          <div>{selectOrderItems}</div>
          <div>{selectSecurityItems}</div>
        </div>

        <div className="flex items-center">
          <div className="flex items-center ml-2">
            <Icon className="text-tradewell text-xl" name="timeline" />
            <div className="p-2">合計損益</div>
          </div>
          <div className="flex ml-auto">
            {orders?.totalProfitOrLoss !== null ? (
              <NumberLabel
                className="text-xl p-2"
                value={
                  orders?.totalProfitOrLoss === 0 &&
                  orders?.pageNum === 1 &&
                  orders?.items.length === 0
                    ? null
                    : orders?.totalProfitOrLoss
                }
                unitName="円"
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-row mx-2 mb-4 mt-6">
        <div className="ml-auto">
          <DisplayableAssetTypeModalButton isMoneytree={false} />
        </div>
      </div>
      {orders !== null && orders.items.length > 0 && (
        <>
          <div>
            {orders.items.map(item => {
              return <OrderItem data={item} />;
            })}
          </div>
          {totalPageCount !== undefined && totalPageCount >= 2 && (
            <div>
              <PaginationOrderList
                startPage={Number(startPage)}
                selectPage={Number(selectPage)}
                totalPageCount={totalPageCount}
                onChangePage={onChangePage}
              />
            </div>
          )}
        </>
      )}
      <div className="flex flex-row pt-2 px-2 text-xs text-bluegray-500">
        <span>※</span>
        <span className="ml-1">
          アセプラ連携後からの取引履歴を確認いただけます。アセプラに連携する前の取引履歴はアセプラに口座を連携した日から遡って過去一年間のみ表示可能です。
        </span>
      </div>
    </div>
  );
};

const filterCategoriesList = {
  chkSettlementSpotSell: 2,
  chkSettlementMerginBuy: 5,
  chkSettlementMerginSell: 6,
  chkMerginBuy: 3,
  chkMerginSell: 4,
  chkSpotBuy: 1,
};

const getOrders = async (
  selectPage: number,
  brokers: api.GetUserBrokerResultItem[] | undefined
) => {
  const orderFilter: OrderFilterState = getOrderSetting(brokers);

  const products = [
    orderFilter.chkStock ? consts.productTypes.国内株式 : null,
    orderFilter.chkFforeignStock ? consts.productTypes.外国株式 : null,
    orderFilter.chkInvestmentTrust ? consts.productTypes.投資信託 : null,
    orderFilter.chkEtfEtn ? consts.productTypes['国内ETF・ETN'] : null,
    orderFilter.chkForeignEtfEtn ? consts.productTypes['外国ETF・ETN'] : null,
    orderFilter.chkReit ? consts.productTypes.国内REIT : null,
    orderFilter.chkForeignReit ? consts.productTypes.外国REIT : null,
    orderFilter.chkEb ? consts.productTypes.国内EB : null,
    orderFilter.chkForeignEb ? consts.productTypes.外国EB : null,
    orderFilter.chkUnlistedStock ? consts.productTypes.未上場株式 : null,
    orderFilter.chkBond ? consts.productTypes.国内債券 : null,
    orderFilter.chkForeignBond ? consts.productTypes.外国債券 : null,
    orderFilter.chkRealEstate ? consts.productTypes.不動産 : null,
    orderFilter.chkOther ? consts.productTypes.その他 : null,
  ].filter(x => x) as number[];

  const categories = [
    orderFilter.chkSpotBuy ? filterCategoriesList.chkSpotBuy : null,
    orderFilter.chkSettlementSpotSell
      ? filterCategoriesList.chkSettlementSpotSell
      : null,
    orderFilter.chkMerginBuy ? filterCategoriesList.chkMerginBuy : null,
    orderFilter.chkMerginSell ? filterCategoriesList.chkMerginSell : null,
    orderFilter.chkSettlementMerginBuy
      ? filterCategoriesList.chkSettlementMerginBuy
      : null,
    orderFilter.chkSettlementMerginSell
      ? filterCategoriesList.chkSettlementMerginSell
      : null,
  ].filter(x => x) as number[];

  const broker = [
    orderFilter.chkSecuritySBI ? consts.MONOBrokerIds.SBI証券 : null,
    orderFilter.chkSecurityRakuten ? consts.MONOBrokerIds.楽天証券 : null,
    orderFilter.chkSecurityMatsui ? consts.MONOBrokerIds.松井証券 : null,
    orderFilter.chkSecurityAuKabuCom
      ? consts.MONOBrokerIds.三菱UFJeスマート証券
      : null,
    orderFilter.chkSecurityNomura ? consts.MONOBrokerIds.野村證券 : null,
  ].filter(x => x) as string[];

  const from =
    orderFilter.FromYM !== ''
      ? getPeriodQueryFormat(orderFilter.FromYM) ?? undefined
      : undefined;
  const to =
    orderFilter.ToYM !== ''
      ? getPeriodQueryFormat(orderFilter.ToYM) ?? undefined
      : undefined;

  const ret = await api.getOrders(
    undefined,
    from && to ? from : undefined,
    from && to ? to : undefined,
    products,
    categories,
    broker,
    selectPage
  );

  const targetYM = getTargetFromToList(ret.OldestDate, ret.LatestDate);

  if (!targetYM.includes(orderFilter.FromYM)) {
    orderFilter.FromYM = targetYM[0];
  }
  if (!targetYM.includes(orderFilter.ToYM)) {
    orderFilter.ToYM = targetYM[targetYM.length - 1];
  }
  localStorage.setItem('order_setting', JSON.stringify(orderFilter));

  const items = ret.Items.map(item => {
    return {
      tradeTime: item.TradeTime,
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      company: item.BrokerName,
      qty: item.Qty,
      price: item.Price,
      openPriceUSD: item.OpenPriceUSD,
      side: item.Side,
      tradeType: item.TradeType,
      profitOrLoss: item.ProfitOrLoss,
      profitOrLossUSD: item.ProfitOrLossUSD,
      accountType: item.AccountType,
      cashMargin: item.CashMargin,
      productType: item.ProductType,
      currencyUnit: item.CurrencyUnit,
    };
  });

  return {
    items: items,
    totalProfitOrLoss: ret.TotalProfitOrLoss,
    oldestDate: ret.OldestDate,
    latestDate: ret.LatestDate,
    totalPageNum: ret.TotalPageNum,
    pageNum: ret.PageNum,
  };
};

export default OrderList;
